import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import api, { tokenStorage, setAuthFailureHandler } from "../../api/client";

const AuthContext = createContext(null);

const USER_KEY = "eduspecial.user";

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const logout = useCallback(async () => {
    try {
      await api.post("/users/logout");
    } catch {
      // A failed server-side logout must not strand the user in a
      // half-signed-in state, so local teardown happens regardless.
    }
    tokenStorage.clear();
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const login = useCallback((data) => {
    const { accessToken, refreshToken } = data;
    tokenStorage.set(accessToken, refreshToken);

    const profile = {
      email: data.email,
      role: data.role,
      firstname: data.firstname,
      lastname: data.lastname,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(profile));
    setUser(profile);
    return profile;
  }, []);

  // When a refresh ultimately fails the client clears tokens; mirror that
  // into context so the UI drops back to the signed-out state immediately.
  useEffect(() => {
    setAuthFailureHandler(() => {
      localStorage.removeItem(USER_KEY);
      setUser(null);
    });
    setIsBootstrapping(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user && tokenStorage.getAccess()),
      isAdmin: user?.role === "admin",
      isBootstrapping,
      login,
      logout,
    }),
    [user, isBootstrapping, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
